import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import { usePlan } from "../hooks/usePlan";
import { PlanBadge, UpgradeModal } from "./UpgradePrompt";
import {
  useReminderSettings,
  useUpdateReminderSettings,
} from "../hooks/useSettings";

interface SettingsModalProps {
  onClose: () => void;
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}> = ({ checked, onChange, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
      checked ? "bg-[#2563EB]" : "bg-[#D1D5DB]"
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-1"
      }`}
    />
  </button>
);

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <div>
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-[#111827]">{title}</h4>
      <p className="text-xs text-[#9CA3AF] mt-0.5">{description}</p>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const fieldCls =
  "mt-1.5 block w-full border border-[#E5E7EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white placeholder:text-[#9CA3AF]";

const REMINDER_OPTIONS = [1, 3, 7, 14, 30, 60, 90];

// ── Default email template (mirrors the backend default layout) ───────────────
const DEFAULT_SUBJECT = "⏰ {{count}} document(s) expiring soon — {{app_name}}";

const DEFAULT_BODY_HTML = `<h2 style="margin:0 0 6px;color:#111827;font-size:22px;font-weight:700;">
  ⏰ Expiry Reminder
</h2>
<p style="margin:0 0 28px;color:#6B7280;font-size:15px;line-height:1.6;">
  Hi {{user_name}}, you have <strong>{{count}} document(s)</strong> that need your attention.
</p>

{{items_table}}

<table cellpadding="0" cellspacing="0">
  <tr>
    <td style="background:#2563EB;border-radius:8px;">
      <a href="{{app_url}}/items"
         style="display:block;padding:12px 28px;color:#ffffff;font-weight:600;font-size:14px;text-decoration:none;">
        View Dashboard →
      </a>
    </td>
  </tr>
</table>`;

// ── Template variable metadata ─────────────────────────────────────────────────
const TEMPLATE_VARS = [
  { variable: "{{user_name}}", label: "Recipient name", example: "Alex" },
  { variable: "{{count}}", label: "Item count", example: "3" },
  {
    variable: "{{items_table}}",
    label: "Items table",
    example: "(HTML table)",
    bodyOnly: true,
  },
  { variable: "{{app_name}}", label: "App name", example: "Sentrilog" },
  {
    variable: "{{app_url}}",
    label: "App URL",
    example: "https://app.example.com",
  },
] as const;

// ── Build a preview-ready HTML by substituting sample values ──────────────────
const SAMPLE_ITEMS_TABLE = `<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:32px;">
  <thead>
    <tr style="border-bottom:2px solid #E5E7EB;">
      <th style="text-align:left;padding:8px 0;font-size:11px;color:#9CA3AF;font-weight:700;text-transform:uppercase;">Document</th>
      <th style="text-align:left;padding:8px 8px;font-size:11px;color:#9CA3AF;font-weight:700;text-transform:uppercase;">Category</th>
      <th style="text-align:left;padding:8px 0;font-size:11px;color:#9CA3AF;font-weight:700;text-transform:uppercase;">Expiry Date</th>
      <th style="text-align:right;padding:8px 0;font-size:11px;color:#9CA3AF;font-weight:700;text-transform:uppercase;">Status</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;font-size:14px;color:#111827;font-weight:500;">Passport</td>
      <td style="padding:14px 8px;border-bottom:1px solid #F3F4F6;font-size:13px;color:#6B7280;">Travel</td>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;font-size:13px;color:#374151;">Mar 15, 2026</td>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;text-align:right;"><span style="background:#FEF2F2;color:#DC2626;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;">Expired</span></td>
    </tr>
    <tr>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;font-size:14px;color:#111827;font-weight:500;">Business License</td>
      <td style="padding:14px 8px;border-bottom:1px solid #F3F4F6;font-size:13px;color:#6B7280;">Legal</td>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;font-size:13px;color:#374151;">Apr 2, 2026</td>
      <td style="padding:14px 0;border-bottom:1px solid #F3F4F6;text-align:right;"><span style="background:#FFFBEB;color:#D97706;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;">7 days left</span></td>
    </tr>
    <tr>
      <td style="padding:14px 0;font-size:14px;color:#111827;font-weight:500;">Insurance Policy</td>
      <td style="padding:14px 8px;font-size:13px;color:#6B7280;">Insurance</td>
      <td style="padding:14px 0;font-size:13px;color:#374151;">May 20, 2026</td>
      <td style="padding:14px 0;text-align:right;"><span style="background:#EFF6FF;color:#2563EB;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;">30 days left</span></td>
    </tr>
  </tbody>
</table>`;

function buildPreviewHtml(subject: string, body: string): string {
  const substituted = body
    .replace(/\{\{user_name\}\}/g, "Alex")
    .replace(/\{\{count\}\}/g, "3")
    .replace(/\{\{items_table\}\}/g, SAMPLE_ITEMS_TABLE)
    .replace(/\{\{app_name\}\}/g, "Sentrilog")
    .replace(/\{\{app_url\}\}/g, "#");

  const subjectPreview = subject
    .replace(/\{\{user_name\}\}/g, "Alex")
    .replace(/\{\{count\}\}/g, "3")
    .replace(/\{\{app_name\}\}/g, "Sentrilog")
    .replace(/\{\{app_url\}\}/g, "#");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <style>body{margin:0;padding:0;background:#F3F4F6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}</style>
</head>
<body>
  <div style="background:#E5E7EB;padding:8px 12px;font-size:11px;color:#6B7280;font-family:monospace;word-break:break-all;">
    <strong>Subject:</strong> ${subjectPreview}
  </div>
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#F3F4F6;padding:20px 16px;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0"
             style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);max-width:560px;width:100%;">
        <tr><td style="background:#2563EB;padding:20px 32px;">
          <div style="font-size:16px;font-weight:700;color:#ffffff;">Sentrilog</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.75);margin-top:2px;">Stay ahead. Avoid fines.</div>
        </td></tr>
        <tr><td style="padding:32px 32px 24px;">${substituted}</td></tr>
        <tr><td style="background:#F9FAFB;padding:16px 32px;border-top:1px solid #E5E7EB;text-align:center;font-size:12px;color:#9CA3AF;">
          © ${new Date().getFullYear()} Sentrilog · All rights reserved
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// Hour options: 0 – 23  →  "12:00 AM" … "11:00 PM"
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const label =
    h === 0
      ? "12:00 AM"
      : h < 12
        ? `${h}:00 AM`
        : h === 12
          ? "12:00 PM"
          : `${h - 12}:00 PM`;
  return { value: h, label };
});

// ── Main ──────────────────────────────────────────────────────────────────────
const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const { user } = useAuth();
  const { canUse, requiredPlanFor } = usePlan();
  const { data: savedSettings, isLoading: isLoadingSettings } =
    useReminderSettings();
  const updateMutation = useUpdateReminderSettings();

  const canUseTemplates = canUse("customReminderTemplates");
  const [showTemplateUpgrade, setShowTemplateUpgrade] = useState(false);

  // ── Notification channels ─────────────────────────────────────────────────
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailAddress, setEmailAddress] = useState(user?.email || "");

  // ── Reminder schedule (loaded from API) ───────────────────────────────────
  const [remindDaysBefore, setRemindDaysBefore] = useState<number[]>([7, 30]);
  const [sendHour, setSendHour] = useState(9);

  // Populate form once API data arrives
  useEffect(() => {
    if (savedSettings) {
      setEmailEnabled(savedSettings.email_enabled);
      setRemindDaysBefore(savedSettings.reminder_days ?? [7, 30]);
      setSendHour(savedSettings.send_hour ?? 9);
    }
  }, [savedSettings]);

  const toggleReminder = (val: number) =>
    setRemindDaysBefore((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );

  // ── Email template ─────────────────────────────────────────────────────────
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (savedSettings) {
      setCustomSubject(savedSettings.custom_subject ?? "");
      setCustomBody(savedSettings.custom_body_html ?? "");
    }
  }, [savedSettings]);

  const insertVar = useCallback(
    (variable: string, target: "subject" | "body") => {
      if (target === "subject" && subjectRef.current) {
        const el = subjectRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const next = el.value.slice(0, start) + variable + el.value.slice(end);
        setCustomSubject(next);
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(
            start + variable.length,
            start + variable.length
          );
        });
      } else if (target === "body" && bodyRef.current) {
        const el = bodyRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const next = el.value.slice(0, start) + variable + el.value.slice(end);
        setCustomBody(next);
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(
            start + variable.length,
            start + variable.length
          );
        });
      }
    },
    []
  );

  // ── Active tab ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<
    "notifications" | "reminders" | "template"
  >("notifications");

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (emailEnabled && !emailAddress.trim()) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        reminder_days: remindDaysBefore,
        email_enabled: emailEnabled,
        send_hour: sendHour,
        custom_subject: customSubject.trim() || null,
        custom_body_html: customBody.trim() || null,
      });
      toast.success("Settings saved");
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to save settings");
    }
  };

  return (
    <>
      <div className="fixed z-50 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-[#111827] bg-opacity-40 transition-opacity"
            onClick={onClose}
          />

          {/* Panel */}
          <div
            className={`inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full ${activeTab === "template" ? "sm:max-w-5xl" : "sm:max-w-lg"}`}
          >
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                  <svg
                    className="h-4.5 w-4.5 text-[#374151]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-[#111827]">
                    Settings
                  </h2>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">
                    Manage notifications and preferences
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────── */}
            <div className="flex border-b border-[#E5E7EB] px-6">
              {(
                [
                  {
                    id: "notifications",
                    label: "Notifications",
                    locked: false,
                  },
                  {
                    id: "reminders",
                    label: "Reminder Schedule",
                    locked: false,
                  },
                  {
                    id: "template",
                    label: "Email Template",
                    locked: !canUseTemplates,
                  },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    if (t.locked) {
                      setShowTemplateUpgrade(true);
                      return;
                    }
                    setActiveTab(t.id);
                  }}
                  className={`px-1 py-3 mr-5 text-sm font-medium border-b-2 transition-colors inline-flex items-center ${
                    activeTab === t.id
                      ? "border-[#2563EB] text-[#2563EB]"
                      : "border-transparent text-[#6B7280] hover:text-[#374151]"
                  }`}
                >
                  {t.label}
                  {t.locked && (
                    <PlanBadge
                      requiredPlan={requiredPlanFor("customReminderTemplates")}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* ── Tab: Notifications ───────────────────────────────────── */}
            {activeTab === "notifications" && (
              <div className="px-6 py-6 space-y-6">
                {/* Email */}
                <Section
                  title="Email Notifications"
                  description="Receive expiry reminders and alerts directly to your inbox."
                >
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                          <svg
                            className="h-4 w-4 text-[#2563EB]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111827]">
                            Email
                          </p>
                          <p className="text-xs text-[#9CA3AF]">
                            Reminders sent via email
                          </p>
                        </div>
                      </div>
                      <Toggle
                        checked={emailEnabled}
                        onChange={setEmailEnabled}
                        id="toggle-email"
                      />
                    </div>

                    {emailEnabled && (
                      <div>
                        <label className="block text-xs font-medium text-[#374151]">
                          Send notifications to
                        </label>
                        <input
                          type="email"
                          value={emailAddress}
                          onChange={(e) => setEmailAddress(e.target.value)}
                          placeholder="you@example.com"
                          className={fieldCls}
                        />
                      </div>
                    )}
                  </div>
                </Section>

                {/* Slack — managed in Team Settings */}
                <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                  <div className="h-8 w-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                    <svg
                      className="h-4 w-4 text-[#D97706]"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#111827]">Slack</p>
                    <p className="text-xs text-[#9CA3AF]">
                      Managed in{" "}
                      <button
                        type="button"
                        className="text-[#2563EB] hover:underline"
                        onClick={() => {
                          onClose();
                          window.location.href = "/team";
                        }}
                      >
                        Team Settings →
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Tab: Reminder Schedule ───────────────────────────────── */}
            {activeTab === "reminders" && (
              <div className="px-6 py-6 space-y-6">
                {isLoadingSettings ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2563EB]" />
                  </div>
                ) : (
                  <>
                    {/* When to remind */}
                    <Section
                      title="When to remind"
                      description="Days before a document expires to trigger a reminder email. Select multiple."
                    >
                      <div className="flex flex-wrap gap-2">
                        {REMINDER_OPTIONS.map((days) => {
                          const active = remindDaysBefore.includes(days);
                          return (
                            <button
                              key={days}
                              type="button"
                              onClick={() => toggleReminder(days)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                                active
                                  ? "bg-[#EFF6FF] border-[#2563EB] text-[#2563EB]"
                                  : "bg-white border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]"
                              }`}
                            >
                              {days} day{days > 1 ? "s" : ""} before
                            </button>
                          );
                        })}
                      </div>

                      {remindDaysBefore.length > 0 && (
                        <div className="mt-2 flex items-start gap-2 px-3 py-2.5 rounded-md bg-[#F0FDF4] border border-[#86EFAC]">
                          <svg
                            className="h-4 w-4 text-[#15803D] mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <p className="text-xs text-[#15803D]">
                            Reminders will be sent{" "}
                            {[...remindDaysBefore]
                              .sort((a, b) => b - a)
                              .map((d) => `${d} day${d > 1 ? "s" : ""}`)
                              .join(", ")}{" "}
                            before expiry.
                          </p>
                        </div>
                      )}

                      {remindDaysBefore.length === 0 && (
                        <p className="text-xs text-[#EF4444] mt-1">
                          Select at least one interval to receive reminders.
                        </p>
                      )}
                    </Section>

                    {/* What time */}
                    <Section
                      title="Daily send time (UTC)"
                      description="The hour the cron job runs to dispatch reminder emails."
                    >
                      <select
                        value={sendHour}
                        onChange={(e) => setSendHour(Number(e.target.value))}
                        className={fieldCls}
                      >
                        {HOUR_OPTIONS.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-[#9CA3AF] mt-1">
                        Currently set to{" "}
                        <strong>{HOUR_OPTIONS[sendHour]?.label}</strong> UTC.
                        The cron runs once per day at this time.
                      </p>
                    </Section>
                  </>
                )}
              </div>
            )}

            {/* ── Tab: Email Template ──────────────────────────────────── */}
            {activeTab === "template" && (
              <div className="flex h-[560px] overflow-hidden">
                {/* ── Left: editor ─────────────────────────────────────── */}
                <div className="w-1/2 flex flex-col border-r border-[#E5E7EB] overflow-y-auto">
                  <div className="px-5 py-4 space-y-5 flex-1">
                    {/* Info banner */}
                    <div className="flex items-start gap-2 p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg text-xs text-[#1D4ED8]">
                      <svg
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        Leave both fields empty to use the default template. Use
                        the variable chips to insert dynamic data.
                      </span>
                    </div>

                    {/* Subject */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-[#374151]">
                          Subject line
                        </label>
                        {customSubject && (
                          <button
                            type="button"
                            onClick={() => setCustomSubject("")}
                            className="text-xs text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <input
                        ref={subjectRef}
                        type="text"
                        value={customSubject}
                        onChange={(e) => setCustomSubject(e.target.value)}
                        placeholder={DEFAULT_SUBJECT}
                        className="block w-full border border-[#E5E7EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                      />
                      {/* Subject variable chips */}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {TEMPLATE_VARS.filter(
                          (v) => !("bodyOnly" in v && v.bodyOnly)
                        ).map((v) => (
                          <button
                            key={v.variable}
                            type="button"
                            onClick={() => insertVar(v.variable, "subject")}
                            title={`Insert ${v.variable} → example: "${v.example}"`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono
                                     bg-[#F3F4F6] border border-[#E5E7EB] rounded text-[#374151]
                                     hover:bg-[#EFF6FF] hover:border-[#93C5FD] hover:text-[#2563EB] transition-colors"
                          >
                            {v.variable}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-[#374151]">
                          Email body{" "}
                          <span className="font-normal text-[#9CA3AF]">
                            (HTML)
                          </span>
                        </label>
                        {customBody && (
                          <button
                            type="button"
                            onClick={() => setCustomBody("")}
                            className="text-xs text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      {/* Body variable chips */}
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {TEMPLATE_VARS.map((v) => (
                          <button
                            key={v.variable}
                            type="button"
                            onClick={() => insertVar(v.variable, "body")}
                            title={`Insert ${v.variable}${v.variable === "{{items_table}}" ? " (renders the full items table)" : ` → example: "${v.example}"`}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono
                                     bg-[#F3F4F6] border border-[#E5E7EB] rounded text-[#374151]
                                     hover:bg-[#EFF6FF] hover:border-[#93C5FD] hover:text-[#2563EB] transition-colors"
                          >
                            {v.variable}
                          </button>
                        ))}
                      </div>
                      <textarea
                        ref={bodyRef}
                        value={customBody}
                        onChange={(e) => setCustomBody(e.target.value)}
                        placeholder={DEFAULT_BODY_HTML}
                        spellCheck={false}
                        className="flex-1 block w-full border border-[#E5E7EB] rounded-md py-2.5 px-3 text-xs
                                 font-mono text-[#374151] bg-white placeholder:text-[#C4C4C4]
                                 focus:outline-none focus:ring-2 focus:ring-[#2563EB] resize-none"
                        style={{ minHeight: "260px" }}
                      />
                      <p className="mt-1.5 text-xs text-[#9CA3AF]">
                        HTML is wrapped in the branded email shell automatically
                        (header + footer). Start with{" "}
                        <code className="bg-[#F3F4F6] px-1 rounded">
                          {"<!DOCTYPE html>"}
                        </code>{" "}
                        to send a fully custom email with no shell.
                      </p>
                    </div>

                    {/* Load default template button */}
                    <button
                      type="button"
                      onClick={() => {
                        setCustomSubject(DEFAULT_SUBJECT);
                        setCustomBody(DEFAULT_BODY_HTML);
                      }}
                      className="text-xs text-[#2563EB] hover:underline"
                    >
                      ← Load default template as starting point
                    </button>
                  </div>
                </div>

                {/* ── Right: live preview ───────────────────────────────── */}
                <div className="w-1/2 flex flex-col bg-[#F3F4F6]">
                  <div className="px-4 py-3 border-b border-[#E5E7EB] bg-white flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#374151]">
                      Live Preview
                    </span>
                    <span className="text-xs text-[#9CA3AF]">sample data</span>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <iframe
                      title="Email preview"
                      sandbox="allow-same-origin"
                      className="w-full h-full border-0"
                      srcDoc={buildPreviewHtml(
                        customSubject || DEFAULT_SUBJECT,
                        customBody || DEFAULT_BODY_HTML
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Footer ───────────────────────────────────────────────── */}
            <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2563EB] rounded-md hover:bg-[#1D4ED8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {updateMutation.isLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showTemplateUpgrade && (
        <UpgradeModal
          feature="Custom Email Templates"
          requiredPlan={requiredPlanFor("customReminderTemplates")}
          description="Personalise your reminder emails with custom subject lines, HTML body, and template variables."
          onClose={() => setShowTemplateUpgrade(false)}
        />
      )}
    </>
  );
};

export default SettingsModal;
